<div class="lm__pageHeader lm__pageHeader--row">
	<div>
		<h1>Pattern Analysis</h1>
		<p>Search logs using pattern text (with &lt;VAR&gt;), then optionally save a reusable pattern.</p>
	</div>

	<button class="lm__tourCta" type="button" (click)="startTour()" aria-label="Open guided tour" title="Shows a guided tour of this page">
    Guided tour
  </button>
</div>

<section class="lm__grid">
	<!-- Search configuration -->
	<article class="lm__card">
		<div class="lm__cardHeader lm__cardHeader--row">
			<div class="lm__cardHeaderTitle">
				<h2>Search</h2>
				<div class="lm__muted">Paste a pattern and search job logs.</div>
			</div>

			<div class="lm__cardHeaderRight">

				<!-- Unique toggle (compact header control) -->
				<label id="tour-unique-toggle" class="lm__headerToggle" title="Return only one row per unique message (server-side)">
					<span>Unique</span>
					<span class="lm__switch lm__switch--sm">
						<input id="uniqueOnly" type="checkbox" [(ngModel)]="uniqueOnly" />
						<span class="lm__slider" aria-hidden="true"></span>
					</span>
				</label>

				<button class="lm__btn" type="button" (click)="openCreatePanel()" [disabled]="(loading$ | async) || !patternTrimmed">
					Create pattern…
				</button>

				<button id="tour-search-btn" class="lm__btn lm__btn--primary" type="button" (click)="search()" [disabled]="(loading$ | async) || !canSearch">
					{{ (loading$ | async) ? 'Searching…' : 'Search' }}
				</button>
			</div>
		</div>

		<div class="lm__cardBody">
			<div *ngIf="seedHint" class="lm__muted" style="margin-bottom: 10px;">
				Loaded from <b>Unmatched</b>. Review the pattern and click <b>Search</b> or open the <b>Create
					Pattern</b> panel.
			</div>

			<!-- Pattern -->
			<label class="lm__label">
				<span class="lm__labelTitle">
					Log Pattern <span class="lm__hint">Use &lt;VAR&gt; for variables</span>
				</span>

				<textarea id="tour-pattern" class="lm__textarea" [(ngModel)]="pattern"
					(ngModelChange)="onPatternChange($event)" rows="6"
					placeholder="Paste a pattern like: Item: '&lt;VAR&gt;' could not be transferred..."></textarea>
			</label>

			<!-- Filters (moved ABOVE preview) -->
			<div id="tour-filters" class="lm__formGrid" style="margin-top: 12px;">
				<label class="lm__label">
					<span class="lm__labelTitle">Job Log Type</span>
					<select class="lm__input" [(ngModel)]="jobLogType">
						<option [ngValue]="null">All (no filter)</option>
						<option *ngFor="let t of jobLogTypeOptions" [ngValue]="t.value">
							{{ t.label }}
						</option>
					</select>
				</label>

				<label class="lm__label">
					<span class="lm__labelTitle">From application</span>
					<select class="lm__input" [(ngModel)]="fromApplicationId">
						<option [ngValue]="null">All</option>
						<option *ngFor="let a of (applications$ | async) ?? []" [ngValue]="a.id">
							{{ a.displayName || a.name }}
						</option>
					</select>
				</label>

				<label class="lm__label">
					<span class="lm__labelTitle">Take</span>
					<input class="lm__input" type="number" [(ngModel)]="take" />
				</label>

				<label class="lm__label">
					<span class="lm__labelTitle">To application</span>
					<select class="lm__input" [(ngModel)]="toApplicationId">
						<option [ngValue]="null">All</option>
						<option *ngFor="let a of (applications$ | async) ?? []" [ngValue]="a.id">
							{{ a.displayName || a.name }}
						</option>
					</select>
				</label>

				<div class="lm__filterActions" style="grid-column: 1 / -1;">
					<button class="lm__btn lm__btn--sm" type="button" (click)="resetFiltersToDefaults()">
						Reset filters
					</button>
					<span class="lm__muted">Resets to JobLogType=Error, take=100</span>
				</div>
			</div>

			<!-- Query preview: collapsible + lower emphasis than filters -->
			<button id="tour-query-preview-toggle" class="lm__disclosure lm__disclosure--inline" type="button" (click)="previewOpen = !previewOpen" *ngIf="pattern.trim()">
				<span class="lm__disclosureChevron" [class.isOpen]="previewOpen">▸</span>
				<span>Query preview</span>
				<span class="lm__muted" style="margin-left:auto;">{{ termsPreview.length }} terms</span>
			</button>

			<div id="tour-query-preview" class="lm__preview" *ngIf="pattern.trim() && previewOpen">
				<div class="lm__previewHeader">
					<div class="lm__previewTitle">Query preview</div>
					<button class="lm__btn lm__btn--sm" type="button" (click)="copyPattern()" [disabled]="!pattern.trim()">
						Copy pattern
					</button>
				</div>

				<div class="lm__previewRow">
					<div class="lm__previewLabel">Static chunks</div>
					<div class="lm__previewValue">
						<span class="lm__mono" *ngIf="chunksPreview.length; else noChunks">
							{{ chunksPreview.join('##VARIABLE##') }}
						</span>
						<ng-template #noChunks><span class="lm__muted">—</span></ng-template>
					</div>
				</div>

				<div class="lm__previewRow">
					<div class="lm__previewLabel">Search terms</div>
					<div class="lm__previewValue">
						<span class="lm__pill lm__pill--term" *ngFor="let t of termsPreview">{{ t }}</span>
						<span class="lm__muted" *ngIf="termsPreview.length === 0">—</span>
					</div>
				</div>

				<div class="lm__warning" *ngIf="patternGenericError">
					{{ patternGenericError }}
				</div>

				<div class="lm__muted lm__kbdHint">
					Tip: <b>Ctrl + Enter</b> to search
				</div>
			</div>

			<!-- Test Pattern: collapsible + below preview -->
			<div class="lm__divider"></div>

			<button id="tour-test-toggle" class="lm__disclosure" type="button" (click)="testOpen = !testOpen">
				<span class="lm__disclosureChevron" [class.isOpen]="testOpen">▸</span>
				<span>Test Pattern</span>
				<span class="lm__muted" style="margin-left:auto;">Optional</span>
			</button>

			<div id="tour-test-panel" class="lm__test" *ngIf="testOpen">
				<div class="lm__testHeader">
					<div>
						<div class="lm__testTitle">Test Pattern</div>
						<div class="lm__muted">Paste a real log line to verify the pattern matches before saving.</div>
					</div>

					<button class="lm__btn" type="button" (click)="testPattern()" [disabled]="(testing$ | async) || !pattern.trim() || !testMessage.trim()">
						{{ (testing$ | async) ? 'Testing…' : 'Test' }}
					</button>
				</div>

				<label class="lm__label" style="margin-top: 10px;">
					<span class="lm__labelTitle">Sample log message</span>
					<textarea class="lm__textarea" rows="5" [(ngModel)]="testMessage"
						placeholder="Paste one job log message here..."></textarea>
				</label>

				<div *ngIf="(testError$ | async) as terr" class="lm__error" style="margin-top: 10px;">
					<span *ngIf="terr">{{ terr }}</span>
				</div>

				<div *ngIf="testResult" class="lm__testResult" [class.isOk]="testResult.isMatch" [class.isBad]="!testResult.isMatch">
					<div class="lm__testPills">
						<span class="lm__pill" [class.lm__pill--ok]="testResult.isMatch" [class.lm__pill--bad]="!testResult.isMatch">
							{{ testResult.isMatch ? 'MATCH' : 'NO MATCH' }}
						</span>

						<span class="lm__pill">
							Strict order: {{ testResult.orderMatch ? 'pass' : 'fail' }}
						</span>
					</div>

					<div class="lm__muted" *ngIf="testResult.notes">{{ testResult.notes }}</div>

					<div class="lm__previewRow" style="margin-top: 8px;">
						<div class="lm__previewLabel">Chunks</div>
						<div class="lm__previewValue">
							<span class="lm__mono">{{ (testResult.chunks ?? []).join(' | ') }}</span>
						</div>
					</div>

					<div class="lm__previewRow">
						<div class="lm__previewLabel">Terms</div>
						<div class="lm__previewValue">
							<span class="lm__pill lm__pill--term" *ngFor="let t of (testResult.terms ?? [])">{{ t
								}}</span>
							<span class="lm__muted" *ngIf="!(testResult.terms ?? []).length">—</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Create panel -->
			<!-- Create panel -->
			<div class="lm__divider"></div>

			<button id="tour-create-toggle" class="lm__disclosure" type="button" (click)="toggleCreatePanel()">
				<span class="lm__disclosureChevron" [class.isOpen]="createPanelOpen">▸</span>
				<span>Create Pattern (optional)</span>
				<span class="lm__muted" style="margin-left:auto;">Title required</span>
			</button>

			<div id="tour-create-panel" class="lm__createPanel" *ngIf="createPanelOpen">

				<div class="lm__formGrid" style="margin-top: 12px;">

					<!-- TITLE -->
					<label class="lm__label" style="grid-column: 1 / -1;">
						<span class="lm__labelTitle">
							Pattern title <span class="lm__required">*</span>
						</span>

						<input id="tour-create-title" class="lm__input" type="text" [(ngModel)]="patternTitle"
							maxlength="200" placeholder="e.g. Exact Online Account Creation Failure" />

						<div class="lm__fieldError" *ngIf="titleError">{{ titleError }}</div>
						<div class="lm__hintBlock">Tip: <b>Ctrl + Shift + S</b> to save</div>
					</label>

					<!-- CATEGORY -->
					<label class="lm__label">
						<span class="lm__labelTitle">Category</span>
						<input id="tour-create-category" class="lm__input" type="text" [(ngModel)]="patternCategory"
							maxlength="100" placeholder="(optional)" />
					</label>

					<!-- STATUS -->
					<label class="lm__label">
						<span class="lm__labelTitle">Status</span>
						<select id="tour-create-status" class="lm__input" [(ngModel)]="patternStatus">
							<option [ngValue]="1">Active</option>
							<option [ngValue]="0">Inactive</option>
						</select>
					</label>
				</div>

				<!-- DESCRIPTION -->
				<label class="lm__label" style="grid-column: 1 / -1; margin-top: 12px;">
					<span class="lm__labelTitle">Description</span>
					<textarea id="tour-create-description" class="lm__textarea" [(ngModel)]="description" rows="5"
						maxlength="4000"
						placeholder="What is this pattern for? How to interpret it? Any action to take?">
		</textarea>
				</label>

				<!-- ARTICLE IDS -->
				<label class="lm__label" style="grid-column: 1 / -1;">
					<span class="lm__labelTitle">Article ID(s)</span>
					<input id="tour-create-articles" class="lm__input" type="text" [(ngModel)]="articleIdsText"
						placeholder="e.g. 103000374329 or 103000374329, 103000123456" />

					<span class="lm__hint">
						Comma / space separated. Use the number from the Freshdesk article URL.<br>
						Example: https://applicationlink-servicedesk.freshdesk.com/a/solutions/articles/103000374329<br>
						The ID is: <b>103000374329</b>
					</span>
				</label>

				<!-- ACTIONS -->
				<div id="tour-create-actions" class="lm__stickyActions" style="position: static; margin-top: 12px;">

					<div class="lm__actionsLeft">
						<button class="lm__btn" type="button" (click)="clearPatternOnly()" [disabled]="(loading$ | async)">
							Clear pattern
						</button>
					</div>

					<div class="lm__actionsRight">
						<button id="tour-create-open-editor" class="lm__btn" type="button" (click)="createPatternInPatterns()" [disabled]="(loading$ | async) || !canCreateOrSave">
							Open Pattern Editor
						</button>

						<button id="tour-create-save" class="lm__btn" type="button" (click)="saveAsPattern()" [disabled]="(loading$ | async) || !canCreateOrSave">
							Save Pattern
						</button>
					</div>
				</div>
			</div>

			<div *ngIf="(error$ | async) as err" class="lm__error" role="alert">
				<span *ngIf="err">{{ err }}</span>
			</div>

			<div *ngIf="(saveMessage$ | async) as msg" class="lm__toast" style="margin-top: 10px;">
				<span *ngIf="msg">{{ msg }}</span>
			</div>
		</div>
	</article>

	<!-- Matches -->
	<article class="lm__card">
		<div class="lm__cardHeader lm__cardHeader--row">
			<div>
				<h2>Matches</h2>
				<div class="lm__muted">
					<ng-container *ngIf="!showUniqueMessages; else uniqLoaded">
						<ng-container *ngIf="results$ | async as r">
							Showing <b>{{ r.length }}</b>
							<ng-container *ngIf="hasTotal$ | async">
								<ng-container *ngIf="total$ | async as t">
									of <b>{{ t }}</b>
								</ng-container>
							</ng-container>
							matches
						</ng-container>
					</ng-container>

					<ng-template #uniqLoaded>
						<ng-container *ngIf="uniqueMessages$ | async as u">
							Showing <b>{{ u.length }}</b>
							<ng-container *ngIf="hasTotal$ | async">
								<ng-container *ngIf="total$ | async as t">
									of <b>{{ t }}</b>
								</ng-container>
							</ng-container>
							unique messages
						</ng-container>
					</ng-template>
				</div>

			</div>

			<div class="lm__cardHeaderActions">
				<button class="lm__btn" type="button" (click)="goUnmatched()">
					View Unmatched
				</button>

				<button id="tour-unique-messages-btn" class="lm__btn" type="button" (click)="toggleUniqueMessages()" [disabled]="(loading$ | async)">
					{{ showUniqueMessages ? 'Show all matches' : 'Show unique messages' }}
				</button>

				<button class="lm__btn" type="button" (click)="goPatterns()">
					View Patterns
				</button>

				<button class="lm__btn" type="button" (click)="exportCsv()" [disabled]="(loading$ | async) || !(results$ | async)?.length">
					Export CSV
				</button>

				<button class="lm__btn" type="button" (click)="loadMore()" *ngIf="(results$ | async)?.length" [disabled]="(loading$ | async)">
					{{ (loading$ | async) ? 'Loading…' : 'Load more' }}
				</button>
			</div>
		</div>

		<div id="tour-matches" class="lm__cardBody">
			<ng-container *ngIf="!showUniqueMessages; else uniqueMode">
				<ng-container *ngIf="results$ | async as results">
					<div class="lm__empty" *ngIf="results.length === 0 && !(loading$ | async)">
						No matches yet. Paste a pattern and click Search.
					</div>

					<div class="lm__results" *ngIf="results.length > 0">
						<div *ngFor="let r of results" class="lm__result">
							<div class="lm__resultMeta">
								<span class="lm__pill">#{{ r.id }}</span>
								<span class="lm__muted">{{ r.creationDate }}</span>

								<span class="lm__pill" *ngIf="r.fromApplicationId != null || r.toApplicationId != null" style="margin-left: 8px;">
									{{ r.fromAppName ?? (r.fromApplicationId != null ? ('#' + r.fromApplicationId) :
									'—') }}
									→
									{{ r.toAppName ?? (r.toApplicationId != null ? ('#' + r.toApplicationId) : '—') }}
								</span>

								<span class="lm__pill" *ngIf="r.linkTypeId != null" style="margin-left: 8px;">
									{{ r.linkTypeName ?? ('LinkType #' + r.linkTypeId) }} sync
								</span>

								<button class="lm__btn lm__btn--sm" type="button" (click)="copyToClipboard(r.message ?? '')" style="margin-left:auto;">
									Copy message
								</button>
							</div>

							<pre class="lm__resultMessage">{{ r.message }}</pre>
						</div>

						<button class="lm__btn lm__btn--full" type="button" (click)="loadMore()" *ngIf="(canLoadMore$ | async)" [disabled]="(loading$ | async)">
							{{ (loading$ | async) ? 'Loading…' : 'Load more results…' }}
						</button>
					</div>
				</ng-container>
			</ng-container>

			<!-- UNIQUE MODE -->
			<ng-template #uniqueMode>
				<ng-container *ngIf="uniqueMessages$ | async as groups">
					<div class="lm__empty" *ngIf="groups.length === 0 && !(loading$ | async)">
						No matches yet.
					</div>

					<div class="lm__results" *ngIf="groups.length > 0">
						<div *ngFor="let g of groups" class="lm__result">
							<div class="lm__resultMeta">
								<span class="lm__pill">{{ g.count }}×</span>
								<span class="lm__muted">Last seen: {{ g.lastSeen }}</span>

								<span class="lm__pill" *ngIf="g.sampleRow.fromApplicationId != null || g.sampleRow.toApplicationId != null" style="margin-left: 8px;">
									{{ g.sampleRow.fromAppName ?? (g.sampleRow.fromApplicationId != null ? ('#' +
									g.sampleRow.fromApplicationId) : '—') }}
									→
									{{ g.sampleRow.toAppName ?? (g.sampleRow.toApplicationId != null ? ('#' +
									g.sampleRow.toApplicationId) : '—') }}
								</span>

								<span class="lm__pill" *ngIf="g.sampleRow.linkTypeId != null" style="margin-left: 8px;">
									{{ g.sampleRow.linkTypeName ?? ('LinkType #' + g.sampleRow.linkTypeId) }} sync
								</span>

								<button class="lm__btn lm__btn--sm" type="button" (click)="copyToClipboard(g.message)" style="margin-left:auto;">
									Copy message
								</button>
							</div>

							<pre class="lm__resultMessage">{{ g.message }}</pre>
						</div>

						<button class="lm__btn lm__btn--full" type="button" (click)="loadMore()" *ngIf="(canLoadMore$ | async)" [disabled]="(loading$ | async)">
							{{ (loading$ | async) ? 'Loading…' : 'Load more results…' }}
						</button>
					</div>
				</ng-container>
			</ng-template>
		</div>
	</article>
</section>


<app-tour-overlay></app-tour-overlay>
